export const INSTALLED_APP_UPDATE_MIN_BACKGROUND_MS = 10 * 60 * 1000
export const INSTALLED_APP_UPDATE_RESUME_WINDOW_MS = 5000
export const INSTALLED_APP_UPDATE_CHECK_TIMEOUT_MS = 4000

export type AppVisibility = 'hidden' | 'visible'

export interface InstalledAppUpdaterOptions {
  currentBuildId: string | null
  isInstalledApp: () => boolean
  now: () => number
  fetchDeployedBuildId: () => Promise<string | null>
  hasUnsentInput: () => boolean
  reload: () => void
  minBackgroundMs?: number
  resumeWindowMs?: number
}

export function createInstalledAppUpdater({
  currentBuildId,
  isInstalledApp,
  now,
  fetchDeployedBuildId,
  hasUnsentInput,
  reload,
  minBackgroundMs = INSTALLED_APP_UPDATE_MIN_BACKGROUND_MS,
  resumeWindowMs = INSTALLED_APP_UPDATE_RESUME_WINDOW_MS,
}: InstalledAppUpdaterOptions) {
  let hiddenAt: number | null = null
  let resumedAt: number | null = null
  let interactedSinceResume = false
  let reloadWhenHidden = false
  let pendingCheck: Promise<void> | null = null

  function canReloadNow() {
    return resumedAt !== null
      && !interactedSinceResume
      && now() - resumedAt <= resumeWindowMs
      && !hasUnsentInput()
  }

  async function checkForNewerBuild() {
    const deployedBuildId = await fetchDeployedBuildId()
    if (!deployedBuildId || deployedBuildId === currentBuildId)
      return
    if (canReloadNow())
      reload()
    else
      reloadWhenHidden = true
  }

  function handleVisibility(visibility: AppVisibility): Promise<void> {
    if (!currentBuildId || !isInstalledApp())
      return Promise.resolve()
    if (visibility === 'hidden') {
      resumedAt = null
      if (reloadWhenHidden && !hasUnsentInput()) {
        reload()
        return Promise.resolve()
      }
      hiddenAt ??= now()
      return Promise.resolve()
    }
    const backgroundedAt = hiddenAt
    hiddenAt = null
    resumedAt = now()
    interactedSinceResume = false
    if (pendingCheck)
      return pendingCheck
    if (backgroundedAt === null || now() - backgroundedAt < minBackgroundMs)
      return Promise.resolve()
    pendingCheck = checkForNewerBuild().finally(() => {
      pendingCheck = null
    })
    return pendingCheck
  }

  function handleInteraction() {
    interactedSinceResume = true
  }

  return { handleVisibility, handleInteraction }
}

export function isRunningAsInstalledApp(target: Window = window): boolean {
  const navigatorWithStandalone = target.navigator as Navigator & { standalone?: boolean }
  return navigatorWithStandalone.standalone === true
    || target.matchMedia?.('(display-mode: standalone)').matches === true
}

export function getRunningBuildId(target: Document = document): string | null {
  for (const script of target.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const pathname = new URL(script.src, target.baseURI).pathname.replace(/^\/+/, '')
    if (pathname && !pathname.startsWith('@'))
      return pathname
  }
  return null
}

const TEXT_INPUT_SELECTOR = [
  'textarea',
  'input:not([type])',
  'input[type="text"]',
  'input[type="search"]',
  'input[type="email"]',
  'input[type="password"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(', ')

export const SAVES_AS_YOU_TYPE_ATTRIBUTE = 'data-saves-as-you-type'

function isEditableText(element: HTMLElement) {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)
    return !element.readOnly && !element.disabled
  return element.isContentEditable
}

function textOf(element: HTMLElement) {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
    ? element.value
    : element.textContent ?? ''
}

export function createUnsentInputTracker() {
  const editedElements = new Set<HTMLElement>()

  function recordInput(target: EventTarget | null) {
    if (!(target instanceof HTMLElement))
      return
    const field = target.closest<HTMLElement>(TEXT_INPUT_SELECTOR)
    if (field && !field.closest(`[${SAVES_AS_YOU_TYPE_ATTRIBUTE}]`))
      editedElements.add(field)
  }

  function hasUnsentInput() {
    for (const element of editedElements) {
      if (!element.isConnected) {
        editedElements.delete(element)
        continue
      }
      if (isEditableText(element) && textOf(element).trim() && element.getClientRects().length > 0)
        return true
    }
    return false
  }

  return { recordInput, hasUnsentInput }
}

export async function fetchDeployedBuildId(
  fetchImpl: typeof fetch = fetch,
  timeoutMs = INSTALLED_APP_UPDATE_CHECK_TIMEOUT_MS,
): Promise<string | null> {
  try {
    const response = await fetchImpl(`/app-version.json?t=${Date.now()}`, {
      cache: 'no-store',
      signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(timeoutMs) : undefined,
    })
    if (!response.ok)
      return null
    const body: unknown = await response.json()
    if (typeof body !== 'object' || body === null)
      return null
    const { buildId } = body as { buildId?: unknown }
    return typeof buildId === 'string' && buildId ? buildId : null
  }
  catch {
    return null
  }
}
